// 10. 10. 2026 (owner, deploy7): a system just opened (by level) or a thing needed now (a worn item, a Blueprint, Relic
// Shards, an item for a tier or a rework) gets a small popup: its name and what it is, `Try it` (opens it, nothing is
// pointed at) and `Later`. It never dims and never blocks the rest of the game; going to another screen while it is up
// puts it away too (ignored). One at a time, the oldest first, and only after the first session's guided steps. Shown,
// tried, put away and ignored are counted for the pilot report.

import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import type { MeDTO } from '@lastday/shared';
import { api } from '@/api/client';
import { qk, useMe } from '@/api/hooks';
import { Button } from '@/components/ui';
import { t } from '@/i18n';

type Action = 'shown' | 'tried' | 'closed' | 'ignored';

export function UnlockPopup() {
  const me = useMe().data?.survivor;
  const busy = !!me?.pendingEncounterId || !!me?.pendingEventExpeditionId;
  const card = me && !me.guided && !busy ? me.scheduleCards[0] : undefined;
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  // The popup on screen and the screen it came up on.
  const up = useRef<{ id: string; path: string } | null>(null);
  // `Try it`'s screen: the router moves there in a transition, after the next popup is already up; that one belongs to it.
  const going = useRef<string | null>(null);

  const act = (id: string, action: Action) => {
    if (action !== 'shown') {
      up.current = null;
      // It goes at once; the server keeps it gone.
      qc.setQueryData<MeDTO>(qk.me, (m) => (m?.survivor ? { ...m, survivor: { ...m.survivor, scheduleCards: m.survivor.scheduleCards.filter((c) => c.id !== id) } } : m));
    }
    void api.post('/schedule/card', { id, action }).then(
      () => (action === 'shown' ? undefined : qc.invalidateQueries({ queryKey: qk.me })),
      () => undefined,
    );
  };

  useEffect(() => {
    if (!card || up.current?.id === card.id) return;
    up.current = { id: card.id, path: going.current ?? pathname };
    going.current = null;
    act(card.id, 'shown');
  }, [card?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Another screen while it is up: put away (ignored).
  useEffect(() => {
    const u = up.current;
    if (going.current === pathname) going.current = null;
    if (u && u.path !== pathname) act(u.id, 'ignored');
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!card) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="unlock-popup"
      data-card={card.id}
      className="pointer-events-auto fixed inset-x-3 bottom-[76px] z-[44] mx-auto flex max-w-[420px] flex-col gap-2 rounded-[8px] border border-line bg-surface p-3.5 shadow-float lg:inset-x-auto lg:bottom-6 lg:right-6 lg:w-[380px]"
    >
      <span className="flex items-baseline gap-2">
        <span className="mono rounded-[2px] bg-text px-1.5 text-[11px] font-bold uppercase leading-[17px] tracking-[0.1em] text-bg">{t('common.unlock.new')}</span>
        <span className="display text-[17px] font-semibold" data-testid="unlock-title">
          {card.title}
        </span>
      </span>
      <span className="text-[14px] leading-5" data-testid="unlock-text">
        {card.text}
      </span>
      <span className="flex gap-2">
        <Button
          size="sm"
          data-testid="unlock-try"
          onClick={() => {
            going.current = new URL(card.to, window.location.origin).pathname;
            act(card.id, 'tried');
            navigate(card.to);
          }}
        >
          {t('common.unlock.try')}
        </Button>
        <Button size="sm" variant="ghost" data-testid="unlock-later" onClick={() => act(card.id, 'closed')}>
          {t('common.unlock.later')}
        </Button>
      </span>
    </div>
  );
}
